/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-20 07:47:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, reset, getFormValues, change } from 'redux-form';
import {
  Page,
  Layout,
  Card,
  PageActions,
  Stack,
  Button,
  Icon,
  TextField,
  Link
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { LockMinor, ExternalSmallMinor, SearchMinor } from '@shopify/polaris-icons';

import MainMenu from 'src/components/containers/main-menu';
import Languages from 'src/components/views/tools/translation/languages';
import DidYouKnow from 'src/components/views/tools/translation/did-you-know';

import ContentTabs from 'src/components/views/tools/translation/tabs';
import ConfirmFullFillModal from 'src/components/views/tools/translation/confirm-full-fill-modal';
import GrantPermissionsNotif from 'src/components/views/tools/translation/grant-permissions-notif';
import { hasPermissionLocales } from 'src/helpers/permission-helper';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import TitleHelp from 'src/components/views/common/title-help';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';

import history from 'src/routes/history';
import { routes } from 'src/routes';
import { URL } from 'src/constants/links';
import {
  buildInitialTranslationValues,
  buildTranslationValues,
  getSortByOptionList,
  buildTranslationFields
} from 'src/helpers/translation-helper';
import { getShop, buildUpdateAppPermissionURL, confirmLeavePage } from 'src/helpers';
import * as FilterSettingsActions from 'actions/filter-settings-actions';
import * as TranslationActions from 'actions/translation-actions';
import rocketIcon from 'assets/images/common/rocket-icon.png';
import LockImage from 'src/assets/images/lock-unlock-image/translation-lock.svg';

import type { ComposedProps, State } from './index.type';

class Translation extends Component<ComposedProps, State> {
  timerTyping = undefined;
  state = {
    // Search string
    inputSearchText: '',

    selectedLocale: '',
    dismissedBanner: false,
    defaultTranslations: {},
    translationFields: {},
    activeFullFillModal: false,
    confirmLocale: '',
    sortByOptions: [],
    recommendationFields: [],

    // Grant permission modal
    activePermissionNotif: false,
    permissionURL: '',
    changePlanFromReviewIOModal: false
  };

  async componentDidMount() {
    const { getFilterSettings, getLocales } = this.props;
    this.setState({ inputSearchText: '' });
    // Hide banner
    this.checkDismissedBanner();

    // Get locales, Get translation from filter-settings-actions
    await Promise.all([getLocales(), getFilterSettings()]);

    // Show Grant permission notification
    this.showGrantPermissionsModalNotif();

    // Get default translation for primary locale
    this.getDefaultTranslationsByLocale(this.props.primaryLocale);

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }
  componentWillReceiveProps(nextProps) {
    const { loadingFilterSettings } = this.props;
    const { primaryLocale, loadingFilterSettings: nextLoadingFilterSettings } = nextProps;
    const { selectedLocale } = this.state;

    // Set selected local after reading locales
    if (primaryLocale && _.isEmpty(selectedLocale)) {
      this.setState({ selectedLocale: primaryLocale });
    }

    // Get Sort by options and recommendation fields list from settings
    if (loadingFilterSettings && !nextLoadingFilterSettings) {
      const { adminAppDisplay, i18n } = nextProps;
      const {
        sortByOptions: sortByOptionDisplay = {},
        recommendationTranslationFields: recommendationFields = []
      } = adminAppDisplay;

      const sortByOptions = getSortByOptionList(sortByOptionDisplay, i18n);
      this.setState({ sortByOptions, recommendationFields });
    }
  }

  render() {
    const {
      i18n,
      dirty,
      pristine,
      loadingFilterSettings,
      savingFilterSettings,
      handleSubmit,
      locales,
      primaryLocale,
      history,
      recommendation,
      enabledFeature,
      themeSetupV3
    } = this.props;
    const {
      selectedLocale,
      translationFields,
      inputSearchText,
      dismissedBanner,
      activePermissionNotif,
      permissionURL
    } = this.state;
    const { name: localeName = '' } = _.find(locales, { locale: selectedLocale }) || {};
    const selectedTab = _.get(this.props.location, 'state.selectedTab', 0);

    return (
      <div className="BC-Page BC-Page__Translation">
        <MainMenu menuId="menu-settings-translation" />
        <Page
          title={i18n.translate('AdminHTML.Translation.Translation.title')}
          primaryAction={
            enabledFeature ? (
              <div className="BC-Page BC-Page__Button">
                <Stack>
                  {locales.length > 0 && !pristine && (
                    <Button disabled={loadingFilterSettings} onClick={this.discardChange}>
                      {i18n.translate('AdminHTML.Common.Action.cancel')}
                    </Button>
                  )}
                  <Button
                    primary
                    onClick={handleSubmit(this.handleSave.bind(this))}
                    disabled={pristine || locales.length <= 0}
                    loading={savingFilterSettings}>
                    {i18n.translate('AdminHTML.Common.Action.save')}
                  </Button>
                </Stack>
              </div>
            ) : null
          }>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={URL['Link_V2_Tool_Translation_ManualGuide']}
            style={{
              marginBottom: 'calc(var(--p-space-4) * 2)'
            }}
          />
          {!enabledFeature ? (
            this.renderLockCard(i18n)
          ) : (
            <>
              <Layout>
                {/* Select languages */}
                <Layout.Section>
                  <Languages
                    locales={locales}
                    onChange={this.handleChangeLocale}
                    localeName={localeName}
                  />
                </Layout.Section>

                <Layout.Section>
                  <div className="BC-Container__Notifications">
                    {/* Installation reminder status banner */}
                    <InstallationRemindStatusBanner />

                    {/* Update permission locales*/}
                    {activePermissionNotif && (
                      <GrantPermissionsNotif
                        permissionURL={permissionURL}
                        onDismiss={this.onDismissUpdatePermissionNotif}
                      />
                    )}

                    {/* Did you know? */}
                    {!dismissedBanner ? <DidYouKnow onDismiss={this.handleDismissBanner} /> : ''}

                    {/* Sticky notification for exceeded plan */}
                    <ExceededPlanNotification />
                  </div>
                </Layout.Section>

                {/* Tabs */}
                <Layout.Section>
                  <Card>
                    <div className="search">
                      <TextField
                        clearButton
                        onChange={this.onSearch}
                        onClearButtonClick={this.handleClearButton}
                        value={inputSearchText}
                        prefix={<Icon source={SearchMinor} color="base" />}
                        placeholder={i18n.translate(
                          'AdminHTML.Translation.Translation.inputSearchText'
                        )}
                      />
                    </div>
                    <ContentTabs
                      inputSearchText={inputSearchText}
                      selectedLocale={selectedLocale}
                      translationFields={translationFields[selectedLocale]}
                      primaryLocale={primaryLocale}
                      selectedTab={selectedTab}
                      recommendation={recommendation}
                      themeSetupV3={themeSetupV3}
                    />
                  </Card>
                </Layout.Section>
              </Layout>

              <PageActions
                primaryAction={{
                  content: i18n.translate('AdminHTML.Common.Action.save'),
                  onAction: handleSubmit(this.handleSave.bind(this)),
                  disabled: pristine || locales.length <= 0,
                  loading: savingFilterSettings
                }}
                secondaryActions={
                  locales.length > 0 && !pristine
                    ? [
                        {
                          content: i18n.translate('AdminHTML.Common.Action.cancel'),
                          onAction: this.discardChange
                        }
                      ]
                    : []
                }
              />
            </>
          )}
        </Page>

        <ConfirmFullFillModal
          active={this.state.activeFullFillModal}
          handleChange={this.handleChangeFullFillModal}
          handleFullFill={this.handleFullFill}
          title={i18n.translate('AdminHTML.Translation.Translation.confirmFullFill_Title')}
          content={i18n.translate('AdminHTML.Translation.Translation.confirmFullFill_Content')}
        />

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
        <ChangePlanFromReviewIOModal
          open={this.state.changePlanFromReviewIOModal}
          onClose={() => this.setState({ changePlanFromReviewIOModal: false })}
        />
      </div>
    );
  }

  // render lock card for case freemium plan
  renderLockCard = i18n => {
    return (
      <div className="pfs-feature-lock-media-card">
        <div className="content">
          <img src={rocketIcon} alt="pfs-rocket-icon" width={24} height={24} />
          {i18n.translate('AdminHTML.Translation.Plan.lockCard_Content01')}
        </div>
        <p>{i18n.translate('AdminHTML.Translation.Plan.lockCard_Content02')}</p>
        <div className="action">
          <div
            className={`Polaris-Button`}
            onClick={() =>
              this.props.useBoltOn
                ? this.setState({ changePlanFromReviewIOModal: true })
                : history.push(routes.subscription.package())
            }>
            <Icon source={LockMinor} />
            {i18n.translate(`AdminHTML.Common.Action.upgradeToUnlock`)}
          </div>
          <Link external url={URL['Link_V2_Tool_Translation_ManualGuide']}>
            {i18n.translate('AdminHTML.Common.Action.learnMore')}
            <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
          </Link>
        </div>
        <div className="media">
          <img src={LockImage} alt="translation-lock" />
        </div>
      </div>
    );
  };
  // Get dismissed banner status
  checkDismissedBanner = () => {
    const shop = getShop();
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(`${shop}-dismissBannerInTranslationPage`);
    } catch (error) {}

    if (dismissed === 'true') {
      this.setState({ dismissedBanner: true });
    }
  };

  // Handle hide banner
  handleDismissBanner = () => {
    const shop = getShop();
    try {
      localStorage.setItem(`${shop}-dismissBannerInTranslationPage`, 'true');
    } catch (error) {}

    // Hide banner
    this.setState({ dismissedBanner: true });
  };

  // Save translations
  handleSave = async formData => {
    const { i18n, updateFilterSettings, filterSettings, primaryLocale } = this.props;
    const { sortByOptions, defaultTranslations, recommendationFields } = this.state;

    // Save translation values to settings
    const dynamicFields = { sortByOptions, recommendationFields };

    const savedSettings = buildTranslationValues(
      i18n,
      formData,
      defaultTranslations,
      primaryLocale,
      filterSettings,
      dynamicFields
    );
    // Save settings
    await updateFilterSettings(savedSettings);
  };

  // Discard change
  discardChange = () => {
    this.props.resetForm();
  };

  // Change locale
  handleChangeLocale = async newLocale => {
    const { translationValues } = this.props;

    // Get default translations for selected label
    // Skip if already read
    await this.getDefaultTranslationsByLocale(newLocale);

    // If default translation is not empty and selected translation values is empty
    // => Show confirm modal before fill default translation
    let defaultTranslationsForSelectedLocale = _.get(this.state.defaultTranslations, newLocale, {});
    if (
      _.isEmpty(translationValues[newLocale]) &&
      !_.isEmpty(defaultTranslationsForSelectedLocale)
    ) {
      this.setState({ confirmLocale: newLocale });
      this.handleChangeFullFillModal();
    } else {
      const selectedTranslationFields = translationValues[newLocale];
      this.processChangeLocale(selectedTranslationFields, newLocale);
    }
  };

  // Process change locale
  processChangeLocale = (selectedTranslationFields, newLocale) => {
    const { selectedLocale } = this.state;
    const { translationValues } = this.props;

    // Get current values from input
    let currentTranslationFields = translationValues[selectedLocale];

    this.setState({ selectedLocale: newLocale });

    // Fill form with new values
    return this.resetFields(
      selectedTranslationFields,
      currentTranslationFields,
      selectedLocale,
      newLocale
    );
  };

  // Get default translations for locale
  getDefaultTranslationsByLocale = async locale => {
    const { defaultTranslations, translationFields, sortByOptions, recommendationFields } =
      this.state;
    const { filterSettings, themeSetupV3 } = this.props;

    if (defaultTranslations[locale]) {
      return;
    }

    // Get default translations for selected locale
    await this.props.getDefaultLabelTranslations(locale);

    const { defaultTranslationsByLocale, i18n } = this.props;
    defaultTranslations[locale] = defaultTranslationsByLocale;

    const dynamicFields = { sortByOptions, recommendationFields };
    const translationFieldsByLocale = buildTranslationFields(
      i18n,
      filterSettings,
      dynamicFields,
      defaultTranslationsByLocale,
      themeSetupV3
    );
    translationFields[locale] = translationFieldsByLocale;

    this.setState({ defaultTranslations, translationFields });
  };

  /**
   * Fill form values by swapping the values between new and old locale
   *
   * @param  {object} oldFields      translation values of previous locale
   * @param  {object} currentFields  translation values of current locale
   * @param  {string} oldLocale      locale before changing
   * @param  {string} currentLocale  current locale
   *
   * @return {object}                updated translation values
   */
  resetFields = (oldFields, currentFields, oldLocale, newLocale) => {
    let allTranslationValues = {};

    // Save current values to old locale
    allTranslationValues[oldLocale] = currentFields;
    this.props.updateField(oldLocale, currentFields);

    // Update and show values for new locale
    allTranslationValues[newLocale] = {};
    if (_.isObject(oldFields)) {
      Object.keys(oldFields).forEach(key => {
        allTranslationValues[newLocale][key] = oldFields[key];
      });
    }

    // Reset fields if this field is empty but existing for previous locale
    if (_.isObject(currentFields)) {
      Object.keys(currentFields).forEach(key => {
        if (!_.has(oldFields, key) && _.has(allTranslationValues[newLocale], key)) {
          allTranslationValues[newLocale][key] = '';
        }
      });
    }

    this.props.updateField(newLocale, allTranslationValues[newLocale]);

    // Just for log
    return allTranslationValues;
  };

  // Handle open/close modal delete
  handleChangeFullFillModal = (status = 'cancel') => {
    const { confirmLocale: newLocale } = this.state;
    const { translationValues } = this.props;

    // Cancel actions, get initial values
    if (status === 'cancel') {
      const selectedTranslationFields = translationValues[newLocale];
      this.processChangeLocale(selectedTranslationFields, newLocale);
    }

    this.setState(({ activeFullFillModal }) => ({
      activeFullFillModal: !activeFullFillModal
    }));
  };

  // Full fill default translations for new locale
  handleFullFill = () => {
    const { confirmLocale: newLocale } = this.state;

    // Get translation value for selected locale
    let defaultTranslationsForSelectedLocale = _.get(this.state.defaultTranslations, newLocale, {});

    // Full fill default translation
    this.processChangeLocale(defaultTranslationsForSelectedLocale, newLocale);

    // Hide modal
    this.handleChangeFullFillModal('ok');
  };

  // Handle hide notification
  onDismissUpdatePermissionNotif = () => {
    this.setState(({ activePermissionNotif }) => ({
      activePermissionNotif: !activePermissionNotif
    }));
  };

  // Show notification
  showGrantPermissionsModalNotif = () => {
    const { requestPermissionLocales, accessScopes } = this.props;

    // Remove localStorage
    try {
      localStorage.removeItem(`${getShop()}-permissionApproval-redirectURI`);
    } catch (error) {}

    // Check permission from access scopes' tenant app
    if (hasPermissionLocales(accessScopes) || !requestPermissionLocales) return;

    // Get update permission link
    const permissionURL = buildUpdateAppPermissionURL(getShop());

    this.setState({ activePermissionNotif: true, permissionURL });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };

  // On search text change
  onSearch = inputSearchText => {
    this.setState({ inputSearchText });
  };

  handleClearButton = () => {
    this.setState({ inputSearchText: '' });
  };
}

const TranslationForm = reduxForm({
  form: 'TranslationForm', // a unique identifier for this form
  enableReinitialize: true
})(Translation);

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Settings
  getFilterSettings: () =>
    dispatch(FilterSettingsActions.getFilterSettings(undefined, ownProps.i18n, true)),
  updateFilterSettings: (filterSettings: Object) =>
    dispatch(
      FilterSettingsActions.updateFilterSettings(
        filterSettings,
        'translation',
        undefined,
        ownProps.i18n
      )
    ),

  // Locales
  getLocales: () => dispatch(TranslationActions.getLocales(ownProps.i18n)),
  getDefaultLabelTranslations: locale =>
    dispatch(TranslationActions.getDefaultLabelTranslations(locale, ownProps.i18n)),

  // Update redux-form field
  updateField: (field, value) => dispatch(change(`TranslationForm`, field, value)),
  resetForm: () => dispatch(reset('TranslationForm'))
});

const mapStateToProps = (state, props) => {
  const { filterSettings, translation, oauth = {}, adminAppDisplay } = state;
  const accessScopes = _.get(oauth, 'tenantApp.accessScopes', {});
  const recommendationFields = _.get(adminAppDisplay, 'recommendationTranslationFields', {});
  const settings = _.get(oauth, 'tenantApp.settings', {});
  const recommendation = _.get(settings, 'recommendation', {});
  const themeSetupV3 = _.get(settings, 'themeSetupV3.settings.enable', false);

  // Get locales
  const {
    locales = [],
    primaryLocale,
    defaultTranslationsByLocale,
    requestPermissionLocales
  } = translation;

  // Get translation values
  const translationValues = getFormValues('TranslationForm')(state);

  return {
    filterSettings: filterSettings.filterSettings,
    initialValues: buildInitialTranslationValues(
      filterSettings.filterSettings,
      recommendationFields,
      locales,
      primaryLocale
    ),
    locales,
    requestPermissionLocales,
    primaryLocale,
    translationValues,
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    enabledFeature: _.get(oauth.tenantApp, 'settings.globalSales.settings.multi-languages', true),
    loadingFilterSettings: _.get(filterSettings, 'loadingFilterSettings', false),
    savingFilterSettings: _.get(filterSettings, 'savingFilterSettings', false),
    adminAppDisplay: _.get(filterSettings, 'adminAppDisplay', {}),
    defaultTranslationsByLocale,
    accessScopes,
    recommendation,
    themeSetupV3
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(TranslationForm));
